import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "~/Layout";
import useDirectors from "../useDirectors";
import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { Skeleton } from "primereact/skeleton";
import VerticalStepper from "~/components/stepper";
import { Card } from "primereact/card";
import "../directors.scss";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import DirectorInformationForm from "./components/DirectorInformationForm";
import IdentificationForm from "./components/IdentificationForm";
import AddressForm from "./components/AddressForm";
import { FormProvider, useForm } from "react-hook-form";
import {
  DirectorFormData,
  EntitiesData,
  StepField,
  Steps,
} from "../types/director";
import ConfirmationDialog from "~/components/confirmation-dialog/ConfirmationDialog";
import { useCallbackPrompt } from "~/hooks/useCallbackPrommpt";
import DirectorAppointmentHistory from "./components/appointment-history";
import AddDirectorAppointmentModal from "../modals/AddDirectorAppointmentModal";
import DirectorRegistered from "./components/DirectorRegistered";
import ToastMessage from "~/components/ToastMessage";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import { triggerGlobalToast } from "~/utils/globalToast";

const DirectorsLayout = () => {
  const navigate = useNavigate();
  const { entityid } = useParams();
  const toastRef = useRef(null);
  const [directorsArr, setDirectorsArray] = useState({
    data: [],
    loading: false,
  });
  const [hasFetchedDuplicates, setHasFetchedDuplicates] = useState(false);

  const steps: Steps[] = [
    {
      label: "Director Information",
      content: () => (
        <DirectorInformationForm
          directorsArr={directorsArr}
          setIsSubmittedSuccessfully={setIsSubmittedSuccessfully}
          entityId={entityid}
        />
      ),
    },
    {
      label: "Appointment History",
      content: () => (
        <DirectorAppointmentHistory
          showModal={setShowAddAppointmentModal}
          appointments={appointments}
          setEditAppointmentEntry={setEditAppointmentEntry}
          setIsEditAppointment={setIsEditAppointment}
          entityId={entityid}
        />
      ),
    },
    {
      label: "Identification",
      content: () => <IdentificationForm entityId={entityid} />,
    },
    {
      label: "Address",
      content: () =>
        !isDirectorRegistered ? (
          <AddressForm entityId={entityid} />
        ) : (
          <DirectorRegistered
            setActiveIndex={setActiveIndex}
            resetRegData={() => {
              handleResetForm();
            }}
            setIsCongrats={() => setIsDirectorRegistered(false)}
            registeredDirector={registeredDirector?.data}
          />
        ),
    },
  ];

  const stepFields: StepField = {
    0: {
      requiredFields: [
        "given_name",
        "email",
        "surname",
        "phone_code",
        "phone_number",
      ],
      fields: [
        "given_name",
        "other_name",
        "surname",
        "preferred_name",
        "email",
        "phone_code",
        "phone_number",
      ],
    },
    1: {
      requiredFields: ["positions"],
      fields: ["positions"],
    },
    3: {
      requiredFields: [
        "address_country",
        "address_line_1",
        "address_postal_code",
      ],
      fields: [
        "address_country",
        "address_line_1",
        "address_line_2",
        "address_state",
        "address_city",
        "address_postal_code",
      ],
    },
  };
  const {
    fetchEntityDetails,
    activeIndex,
    setActiveIndex,
    handleBack,
    handleNext,
    isStepValid,
    setIsStepValid,
    submitDirector,
    isSubmitting,
    showCancelModal,
    cancelAppointment,
    setCancelAppointment,
    showAddAppointmentModal,
    setShowAddAppointmentModal,
    appointments,
    setAppointments,
    editAppointmentEntry,
    setEditAppointmentEntry,
    isEditAppointment,
    setIsEditAppointment,
    isDirectorRegistered,
    setIsDirectorRegistered,
    registeredDirector,
  } = useDirectors(steps);
  const isLastStep = activeIndex === steps.length - 1;
  const methods = useForm<DirectorFormData>({
    mode: "onChange",
    defaultValues: {
      given_name: "",
      other_name: "",
      surname: "",
      preferred_name: "",
      email: "",
      phone_code: "",
      phone_number: "",
      positions: appointments,
      social_security_number: "",
      passport_number: "",
      country_of_issue: null,
      nationality: null,
      date_of_birth: null,
      expiry_date: null,
      address_country: null,
      address_line_1: "",
      address_line_2: "",
      address_state: null,
      address_city: null,
      address_postal_code: "",
    },
  });
  const {
    watch,
    reset,
    formState: { errors, isDirty },
  } = methods;
  const formValues = watch();
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    isDirty && !isSubmittedSuccessfully,
  ) as [boolean, () => void, () => void];
  const [entityData, setEntityData] = useState<EntitiesData | null>(null);
  const getEntityBreadcrumb = () => {
    if (!entityData) return;
    const countryName = entityData.country ? getName(entityData.country) : "";
    const countryFlag = entityData.country && (
      <ReactCountryFlag countryCode={entityData.country} svg />
    );
    return (
      <span className="flex gap-1 link__style pointer">
        {entityData.name}({countryFlag}
        {countryName})
      </span>
    );
  };
  const breadcrumbs = [
    {
      label: <span className="link__style flex pointer">My Entities</span>,
      command: () => navigate("/entities"),
    },
    {
      label: entityData ? (
        getEntityBreadcrumb()
      ) : (
        <Skeleton width="100px" height="20px" className="mr-1" />
      ),
      command: () => navigate(`/entity/${entityid}/dashboard`),
    },
    {
      label: (
        <span className="link__style flex pointer">
          Appointments & Ownership
        </span>
      ),
      command: () => navigate(`/entity/${entityid}/appointments-ownership`),
    },
    {
      label: "Add New Director",
    },
  ];

  const fetchEntity = async () => {
    try {
      if (entityid) {
        const response = await fetchEntityDetails(entityid);
        if (response?.success) {
          setEntityData(response?.data);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };
  const onSubmit = async (_data: DirectorFormData) => {
    if (!entityid) return;

    const data = {
      ..._data,
      positions: appointments,
    };

    try {
      const response = await submitDirector(data, entityid);

      if (response?.success) {
        setIsSubmittedSuccessfully(true);
        setIsDirectorRegistered(true);
        if (toastRef.current) {
          toastRef.current?.show({
            severity: "success",
            summary: "Director registered.",
            detail: "Your appointment has been created.",
            icon: "pi pi-check",
          });
        }
      }
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Something went wrong while saving director’s information",
        detail: "Please check the fields and try again.",
        icon: "pi pi-times-circle",
      });
      console.error("Add Director failed:", error);
    }
  };

  useEffect(() => {
    fetchEntity();
  }, [entityid]);

  useEffect(() => {
    if (activeIndex === 1) {
      setIsStepValid(Array.isArray(appointments) && appointments.length > 0);
      return;
    }
    const currentStepFields = stepFields[activeIndex]?.fields || [];
    const requiredFields = stepFields[activeIndex]?.requiredFields || [];

    // Check if required fields of the current step are filled
    const requiredFieldsFilled = requiredFields.every(
      (field) => formValues[field as keyof DirectorFormData],
    );

    // Check if there are any errors in current step fields
    const hasErrors = currentStepFields.some(
      (field) => errors[field as keyof DirectorFormData],
    );

    const isCurrentStepValid = requiredFieldsFilled && !hasErrors;
    setIsStepValid(isCurrentStepValid);
  }, [formValues, errors, activeIndex, setIsStepValid]);
  const handleResetForm = () => {
    reset();
    setAppointments([]);
    setIsSubmittedSuccessfully(false);
  };
  const fetchDuplicateDirectors = async () => {
    setDirectorsArray((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const { data } = await apiService(
        API_ROUTES_V2.FETCH_DUPLICATE_DIRECTORS,
        "POST",
        {
          body: {
            given_name: formValues.given_name,
            surname: formValues.surname,
            entity_id: entityid,
          },
          replace: {
            tenantId: entityData.tenant_id || "",
          },
          suppressToast: true,
        },
      );
      setDirectorsArray({
        data: data || [],
        loading: false,
      });
      if (data.length > 0) {
        const isAllLinkedToEntity = data.every(
          (director) => director.is_linked_to_entity,
        );
        if (isAllLinkedToEntity) {
          triggerGlobalToast({
            severity: "warn",
            summary: "This Director has been already appointed to this entity.",
            detail:
              "Review the info to avoid duplicates, or register if you believe there's an error.",
            life: 5000,
          });
        } else {
          triggerGlobalToast({
            severity: "warn",
            summary:
              "This Director might already exist but isn't linked to this entity.",
            detail:
              "Review the info to avoid duplicates, or register if you believe there's an error.",
            life: 5000,
          });
        }
      } else {
        handleNext();
      }
      return data;
    } catch (error) {
      setDirectorsArray({
        data: [],
        loading: false,
      });
      triggerGlobalToast({
        severity: "error",
        summary: "Error",
        detail: "Error fetching duplicate directors",
        life: 5000,
      });
      console.error("Error fetching duplicate directors:", error);
    }
  };
  useEffect(() => {
    if (activeIndex === 0) {
      setHasFetchedDuplicates(false);
      setDirectorsArray({
        data: [],
        loading: false,
      });
    }
  }, [formValues.given_name, formValues.surname]);

  return (
    <Layout crumbs={breadcrumbs}>
      <FormProvider {...methods}>
        <div className="grid director-layout-conatiner">
          <div className="col-3">
            <Card className="stepper-card flex">
              <div className="flex flex-column gap-2">
                <p className="header m-0">Add New Director</p>
                <p className="description m-0">
                  Complete the necessary steps to appoint a new director to this
                  company.
                </p>
              </div>
              <Divider className="m-2" />
              <div className="step-menu">
                <VerticalStepper options={steps} activeIndex={activeIndex} />
              </div>
            </Card>
          </div>
          <div className="col-9">
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Card className="card-onboard">
                {steps[activeIndex].content()}
              </Card>
            </form>
            {!isDirectorRegistered && (
              <div className="next-btn-footer">
                <div className="d-flex justify-content-start">
                  {activeIndex !== 0 && (
                    <Button
                      className="generic-cancel"
                      onClick={showCancelModal}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
                <div className="flex justify-content-end gap-2">
                  {activeIndex !== 0 ? (
                    <Button
                      onClick={handleBack}
                      disabled={activeIndex === 0}
                      className="cancel-btns"
                    >
                      Back
                    </Button>
                  ) : (
                    <Button onClick={showCancelModal} className="cancel-btns">
                      Cancel
                    </Button>
                  )}
                  {!isLastStep ? (
                    <Button
                      disabled={!isStepValid}
                      loading={directorsArr.loading}
                      className={`add-new-contact icon__right ${!isStepValid ? "disable" : ""}`}
                      onClick={async () => {
                        if (activeIndex === 0 && !hasFetchedDuplicates) {
                          await fetchDuplicateDirectors();
                          setHasFetchedDuplicates(true);
                          return;
                        }
                        handleNext();
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      disabled={!isStepValid || isSubmitting}
                      className={`add-new-contact confirm-appointment ${!isStepValid ? "disable" : ""}`}
                      onClick={methods.handleSubmit(onSubmit)}
                      loading={isSubmitting}
                    >
                      Confirm Appointment
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <ToastMessage ref={toastRef} />
        <ConfirmationDialog
          show={cancelAppointment || showPrompt}
          onConfirm={() => {
            // Reset form first to make isDirty false and prevent blocker from triggering again
            reset();
            if (cancelAppointment) {
              setCancelAppointment(false);
              // Navigate with skipBlocker to prevent useCallbackPrompt from triggering
              navigate(`/entity/${entityid}/appointments-ownership`, {
                state: { skipBlocker: true },
              });
            } else {
              confirmNavigation();
            }
          }}
          onCancel={() => {
            if (cancelAppointment) {
              setCancelAppointment(false);
            } else {
              cancelNavigation();
            }
          }}
        />
      </FormProvider>
      <AddDirectorAppointmentModal
        visible={showAddAppointmentModal}
        onHideModal={() => {
          setIsEditAppointment(false);
          setEditAppointmentEntry(null);
          setShowAddAppointmentModal(false);
        }}
        setAppointments={setAppointments}
        editAppointmentEntry={editAppointmentEntry}
        isEditAppointment={isEditAppointment}
        appointments={appointments}
        entityId={entityid}
      />
    </Layout>
  );
};

export default DirectorsLayout;
